/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  WORK_DAYS_OPTIONS,
  TIME_PICKER_FORMAT,
  TRACKING,
  SETTING_NAMES,
  METRICS_CALCULATION_STRATEGY,
  BENCHMARK_ORG_SIZE
} from 'common/constants/constants';
import { formatUTCToTimezone } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import TimePicker from 'components/base/TimePicker';
import { DefinitionLevels } from 'common/services/settings.service';
import { getFormattedDailyTime } from './general-settings.service';
import ConnectSlackCTA from './ConnectSlackCTA';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig.js';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';
import {
  FEATURE_FLAGS_NAMES,
  SettingValues,
  MEDIAN_VALUES
} from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { useSelector } from 'react-redux';
import { isAdminSelector, isSuperUserSelector } from 'state/selectors/account';
import useDoraFeatureFlags from 'views/root/views/home/hooks/useDoraFeatureFlags';
import { ExportAuditLogs } from './ExportAuditLogs/ExportAuditLogs';
import {
  LBButton,
  LBLabel,
  LBSelect,
  LBSelectContent,
  LBSelectGroup,
  LBSelectItem,
  LBSelectTrigger,
  LBSelectValue,
  LBSwitch,
  lbToast
} from '@linearb/buzz';
import { organizationSelector } from 'state/selectors/organization';

interface CustomizationOptions {
  workingDays?: string;
  benchmarkOrgSize?: string;
  metricsBenchmarksEnabled?: boolean;
  metricsCalculationStrategy?: string;
  dailyTime?: unknown;
  displayDailyTime?: string;
  showPersonalInformation?: boolean;
  hideNonWorkingDays?: boolean;
  aiServices?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export default function General({
  selectedTeam,
  isOrgSettings,
  slackSensorAuhorization
}) {
  const isTeamSettings = !isOrgSettings;
  const isAdmin = useSelector(isAdminSelector);
  const isSuperUser = useSelector(isSuperUserSelector);
  const selectedTeamId = selectedTeam?.id;
  const organization = useSelector(organizationSelector);
  const { getSettingOrDefinitionValue, updateSetting, getTeamOrOrgSetting } =
    useSettingsService();
  const isHideNonWorkingDayEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.hideNonWorkingDayEnabled
  });
  const isExportAuditLogsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isExportAuditLogsEnabled
  });
  const aiServicesSettingsVisible = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.aiServicesSettingsVisible
  });
  const aiServicesEnabled = organization?.license?.is_ai_enabled;

  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  // @ts-expect-error
  const workingDays = getTeamOrOrgSetting({
    name: 'workingDays',
    ...(isOrgSettings
      ? { definitionLevel: DefinitionLevels.ORGANIZATION }
      : { definitionLevel: DefinitionLevels.TEAM, teamId: selectedTeamId })
  });
  const metricsBenchmarksEnabledOrgLevel = getSettingOrDefinitionValue({
    name: SETTING_NAMES.METRICS_BENCHMARKS_ENABLED,
    definitionLevel: DefinitionLevels.ORGANIZATION
  });
  const metricsBenchmarksEnabled = getSettingOrDefinitionValue({
    name: SETTING_NAMES.METRICS_BENCHMARKS_ENABLED,
    definitionLevel: isTeamSettings
      ? DefinitionLevels.TEAM
      : DefinitionLevels.ORGANIZATION,
    ...(isTeamSettings ? { teamId: selectedTeamId } : {})
  });

  const hideNonWorkingDays = getSettingOrDefinitionValue({
    name: SETTING_NAMES.HIDE_NON_WORKING_DAY,
    definitionLevel: DefinitionLevels.ORGANIZATION
  });

  const aiServices = getSettingOrDefinitionValue({
    name: SettingValues.AI_SERVICES,
    definitionLevel: DefinitionLevels.ORGANIZATION
  });

  const dailyTime = getFormattedDailyTime({
    getTeamOrOrgSetting,
    isOrgSettings,
    selectedTeamId
  });

  const displayDailyTime = formatUTCToTimezone(dailyTime, TIME_PICKER_FORMAT);

  const [customizationOptions, setCustomizationOptions] =
    useState<CustomizationOptions>({});

  const p90AggregationEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.p90AggregationEnabled
  });
  const { isAllControbutorsTeamSelected } = useDoraFeatureFlags();

  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  const showPersonalInformation = getSettingOrDefinitionValue({
    name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION
  });

  const isDevCoachingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isDevCoachingEnabled
  });

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: isOrgSettings
        ? TRACKING.PAGE_TABS.ORG_GENERAL_SETTINGS
        : TRACKING.PAGE_TABS.TEAM_GENERAL_SETTINGS
    });

    setCustomizationOptions({
      workingDays,
      dailyTime,
      displayDailyTime,
      metricsBenchmarksEnabled,
      showPersonalInformation,
      metricsCalculationStrategy,
      hideNonWorkingDays,
      benchmarkOrgSize,
      aiServices: aiServices?.enabled ?? false
    });
  }, [isOrgSettings, selectedTeam]); // eslint-disable-line

  const timePickerOnChange = (time, dbTime) =>
    setCustomizationOptions({
      ...customizationOptions,
      dailyTime: dbTime,
      displayDailyTime: time
    });

  const saveSettings = async () => {
    const onSuccess = () =>
      lbToast({
        title: 'Success!',
        description: 'Settings saved, and will take effect soon'
      });
    const onFailure = () =>
      lbToast({
        title: 'Oops!',
        description: 'We were unable to update your Company settings'
      });

    try {
      if (isOrgSettings) {
        const settingUpdates = {
          workingDays: customizationOptions['workingDays'],
          dailyTime: customizationOptions['dailyTime'],
          [SettingValues.METRICS_CALCULATION_STRATEGY]:
            customizationOptions.metricsCalculationStrategy,
          [SETTING_NAMES.METRICS_BENCHMARKS_ENABLED]:
            customizationOptions.metricsBenchmarksEnabled,
          [SETTING_NAMES.SHOW_PERSONAL_INFORMATION]:
            customizationOptions.showPersonalInformation,
          [SETTING_NAMES.HIDE_NON_WORKING_DAY]:
            customizationOptions.hideNonWorkingDays,
          [SettingValues.AI_SERVICES]: {
            enabled: customizationOptions.aiServices
          },
          [SettingValues.BENCHMARK_ORG_SIZE]:
            customizationOptions.benchmarkOrgSize
        };
        await Promise.all(
          Object.keys(settingUpdates).map(key =>
            updateSetting(key, settingUpdates[key])
          )
        );
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.ORG_GENERAL_SETTINGS,
          click_type: TRACKING.CLICK.SAVE,
          selected_daily_time: customizationOptions.dailyTime
        });
      } else {
        const settingUpdates = {
          workingDays: customizationOptions['workingDays'],
          dailyTime: customizationOptions['dailyTime'],
          timeZone: moment.tz.guess(),
          [SETTING_NAMES.METRICS_BENCHMARKS_ENABLED]:
            customizationOptions.metricsBenchmarksEnabled
        };
        await Promise.all(
          Object.keys(settingUpdates).map(key =>
            updateSetting(
              key,
              settingUpdates[key],
              DefinitionLevels.TEAM,
              selectedTeamId
            )
          )
        );
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.TEAM_GENERAL_SETTINGS,
          click_type: TRACKING.CLICK.SAVE,
          selected_daily_time: customizationOptions.dailyTime
        });
      }
      onSuccess();
    } catch (e) {
      onFailure();
    }
  };

  const settingsChanged = () => {
    return (
      customizationOptions.workingDays === workingDays &&
      customizationOptions.displayDailyTime === displayDailyTime &&
      customizationOptions.metricsBenchmarksEnabled ===
        metricsBenchmarksEnabled &&
      customizationOptions.showPersonalInformation ===
        showPersonalInformation &&
      customizationOptions.metricsCalculationStrategy ===
        metricsCalculationStrategy &&
      customizationOptions.hideNonWorkingDays === hideNonWorkingDays &&
      customizationOptions.benchmarkOrgSize === benchmarkOrgSize &&
      customizationOptions.aiServices === aiServices?.enabled
    );
  };

  const isBenchmarksSettingsDisabled =
    isTeamSettings && !metricsBenchmarksEnabledOrgLevel; // if orgLevel=false -> cannot modify team level

  return (
    <div className='pt-8 flex flex-col gap-8'>
      <h1 className='text-xl flex justify-between'>
        Weekdays and hours
        <LBButton
          disabled={settingsChanged()}
          onClick={() => {
            saveSettings();
          }}
          data-cy='save-settings-button'
        >
          Save Settings
        </LBButton>
      </h1>

      {isHideNonWorkingDayEnabled && isOrgSettings && (
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <LBSwitch
              checked={customizationOptions.hideNonWorkingDays === true}
              id='hideNonWorkingDays'
              disabled={!isHideNonWorkingDayEnabled}
              onCheckedChange={checked => {
                setCustomizationOptions({
                  ...customizationOptions,
                  hideNonWorkingDays: checked
                });
              }}
              data-cy='exclude-cycle-time-calc-weekends'
            />
            <LBLabel htmlFor='hideNonWorkingDays'>
              Exclude Cycle Time calculation on weekends
            </LBLabel>
          </div>
          <span className='text-muted-foreground'>
            Each team will define their weekends in Team Settings. This will
            apply for Cycle Time and its’ components.
          </span>
        </div>
      )}

      {isHideNonWorkingDayEnabled && !isOrgSettings && (
        <span>
          Exclude weekends has been enabled in your Company Settings. <br />
          This will exclude weekends from your teams cycle time calculation.{' '}
          <br />
          Please define your teams working days.
        </span>
      )}

      <div className='flex items-center gap-2'>
        <LBLabel>What are your work days?</LBLabel>

        <LBSelect
          value={customizationOptions?.workingDays}
          onValueChange={selected => {
            setCustomizationOptions({
              ...customizationOptions,
              workingDays: selected
            });
          }}
        >
          <LBSelectTrigger className='w-48' data-cy='work-days-dropdown'>
            <LBSelectValue />
          </LBSelectTrigger>
          <LBSelectContent>
            <LBSelectGroup>
              {WORK_DAYS_OPTIONS.map(option => (
                <LBSelectItem
                  value={option.value}
                  key={option.value}
                  data-cy={option.value}
                >
                  {option.displayValue}
                </LBSelectItem>
              ))}
            </LBSelectGroup>
          </LBSelectContent>
        </LBSelect>
      </div>

      <div className='flex flex-col'>
        <span className='text-muted-foreground'>
          {isOrgSettings
            ? "We'll help you prep for your daily standup by providing the right info at the right time"
            : "We'll send you a daily digest 15 min before your meeting to help you prepare"}
        </span>
        <div className='flex items-center gap-2'>
          <LBLabel>What time is your daily meeting? </LBLabel>
          <TimePicker
            time={customizationOptions.displayDailyTime}
            onChange={timePickerOnChange}
            dataCy={'daily-meeting-time'}
          />
          {!slackSensorAuhorization && (
            <span className='ml-2'>
              <ConnectSlackCTA />
            </span>
          )}
        </div>
      </div>

      <div className='flex flex-col'>
        <h3 className='text-lg mb-2'>Benchmarks</h3>

        {isBenchmarksSettingsDisabled && (
          <span>Turned off for all teams, contact administrator to enable</span>
        )}

        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <LBSwitch
              checked={customizationOptions.metricsBenchmarksEnabled === true}
              id='metricsBenchmarksEnabled'
              disabled={isBenchmarksSettingsDisabled}
              onCheckedChange={checked => {
                setCustomizationOptions({
                  ...customizationOptions,
                  metricsBenchmarksEnabled: checked
                });
              }}
              data-cy='metrics-benchmarks-switch'
            />
            <LBLabel htmlFor='metricsBenchmarksEnabled'>
              Engineering Metric Benchmarks
            </LBLabel>
          </div>

          <span className='text-muted-foreground'>
            Understand how your {isOrgSettings ? 'teams are' : 'team is'}{' '}
            performing against industry benchmarks
          </span>

          {isOrgSettings && (
            <div className='flex flex-col'>
              <div className='flex items-center gap-2'>
                <LBLabel>Present benchmarks</LBLabel>
                <LBSelect
                  disabled={!customizationOptions.metricsBenchmarksEnabled}
                  value={customizationOptions?.benchmarkOrgSize}
                  onValueChange={selected => {
                    setCustomizationOptions({
                      ...customizationOptions,
                      benchmarkOrgSize: selected
                    });
                  }}
                >
                  <LBSelectTrigger
                    className='w-56'
                    data-cy='present-benchmark-dropdown'
                  >
                    <LBSelectValue />
                  </LBSelectTrigger>
                  <LBSelectContent>
                    <LBSelectGroup>
                      {BENCHMARK_ORG_SIZE.map(option => (
                        <LBSelectItem
                          value={option.value}
                          key={option.value}
                          data-cy={option.value}
                        >
                          {option.displayValue}
                        </LBSelectItem>
                      ))}
                    </LBSelectGroup>
                  </LBSelectContent>
                </LBSelect>
              </div>
            </div>
          )}
        </div>
      </div>
      {isOrgSettings && (
        <div className='flex flex-col'>
          <h3 className='text-lg mb-2'>Metrics calculation</h3>

          <div className='flex items-center gap-2'>
            <LBLabel>Calculate and present metrics by</LBLabel>
            <LBSelect
              value={customizationOptions?.metricsCalculationStrategy}
              onValueChange={selected => {
                setCustomizationOptions({
                  ...customizationOptions,
                  metricsCalculationStrategy: selected
                });
              }}
            >
              <LBSelectTrigger
                className='w-[350px]'
                data-cy='metrics-calculation-dropdown'
              >
                <LBSelectValue />
              </LBSelectTrigger>
              <LBSelectContent>
                <LBSelectGroup>
                  {METRICS_CALCULATION_STRATEGY.filter(
                    item =>
                      p90AggregationEnabled || item.value !== MEDIAN_VALUES.p90
                  ).map(option => (
                    <LBSelectItem
                      value={option.value}
                      key={option.value}
                      data-cy={option.value}
                    >
                      {option.displayValue}
                    </LBSelectItem>
                  ))}
                </LBSelectGroup>
              </LBSelectContent>
            </LBSelect>
          </div>
          <span className='text-muted-foreground'>
            Applies for: PR size, Cycle time, Coding time, Pickup time, Review
            time and Deploy time
          </span>
        </div>
      )}

      {(isAdmin || isSuperUser) &&
        !isAllControbutorsTeamSelected &&
        isOrgSettings && (
          <div
            className='flex flex-col'
            data-testid='settings-hide-personal-data'
          >
            <h3 className='text-lg mb-2'>Individual metrics</h3>
            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <LBSwitch
                  checked={!!customizationOptions.showPersonalInformation}
                  id='showPersonalInformation'
                  onCheckedChange={checked => {
                    setCustomizationOptions({
                      ...customizationOptions,
                      showPersonalInformation: checked
                    });
                  }}
                  data-cy='individual-metrics-switch'
                />
                <LBLabel htmlFor='showPersonalInformation'>
                  Show individual metrics
                </LBLabel>
              </div>
              <span className='text-muted-foreground'>
                Team members individual metrics will be shown in Metrics
                Dashboard, Team Activity
                {isDevCoachingEnabled ? ' and Developer Coaching' : ''}
              </span>
            </div>
          </div>
        )}

      {aiServicesSettingsVisible && (
        <div className='flex flex-col gap-1'>
          <h3 className='text-lg mb-2'>AI Services</h3>
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-2'>
              <LBSwitch
                checked={customizationOptions.aiServices === true}
                disabled={!aiServicesEnabled}
                id='aiSettings'
                onCheckedChange={checked => {
                  setCustomizationOptions({
                    ...customizationOptions,
                    aiServices: checked
                  });
                }}
              />
              <LBLabel htmlFor='aiSettings'>Enable LinearB AI Services</LBLabel>
            </div>
            <span className='text-muted-foreground'>
              {aiServicesEnabled ? (
                <> Control LinearB AI services for your organization</>
              ) : (
                <>
                  Contact support to enable LinearB AI Services for your
                  organization
                </>
              )}
            </span>
          </div>
        </div>
      )}
      {isExportAuditLogsEnabled && (
        <ExportAuditLogs
          selectedTeam={selectedTeam}
          isTeamSettings={isTeamSettings}
        />
      )}
    </div>
  );
}
